// Bileli · modules/live/model — repository over the backend's LIVE-SESSION lifecycle. Transport is hybrid
// per `kind`: EXTERNAL = a Zoom/Meet joinUrl; TELEGRAM_CALL = a native group call on the cohort's space
// (joinUrl null — web-a hosts it). Teacher-only mutations (create / go-live / end / recording / delete,
// editor-gated server-side); participants read the cohort list; GET /me/live serves the student's
// currently-LIVE sessions enriched with courseId/courseName/spacePeerId. Contract: openapi.yaml
// (live-meetings P1, #241) — the backend owns scheduling + lifecycle.

import { z } from 'zod';

import { bileliApiJson, bileliApiVoid, parseApi } from '../../../shared';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const LiveSessionSchema = z.object({
  id: z.string(),
  cohortId: z.string(),
  hostUserId: z.string(),
  title: z.string(),
  status: z.string(), // SCHEDULED | LIVE | ENDED — kept open so a new server status degrades
  kind: z.string().nullish(), // EXTERNAL | TELEGRAM_CALL — absent/unknown ⇒ treat as EXTERNAL
  startsAt: z.string().nullish(),
  // joinUrl is null for a TELEGRAM_CALL. Normalize null→undefined so it stays assignable to DOM
  // `href` / `window.open` (both string | undefined) at every call site, incl. the frozen fallback twin.
  joinUrl: z.string().nullish().transform((v) => v ?? undefined),
  recordingUrl: z.string().nullish(),
  notes: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
  courseId: z.string().nullish(), // set only by GET /me/live
  courseName: z.string().nullish(), // set only by GET /me/live
  spacePeerId: z.number().nullish(), // -100… peer of the cohort space; set only by GET /me/live
  // Join-request invite link for the cohort space (#262). Set only by GET /me/live, null until the
  // space is READY — the banner's route for an enrolled student who never opened the forum.
  spaceInviteLink: z.string().nullish(),
  // Bileli 2026-07-29 (class-diary round): message-thread id of the space's «Жандуу сабактар» topic, so a
  // click lands on the lesson rather than the forum's topic list.
  // ⚠ null is LEGAL on a fully provisioned space — the topic is an optional capability the drain acks,
  // and forums created before it existed have none. Read null as "open the forum", NEVER as "there is no
  // space": spacePeerId may be perfectly good.
  liveTopicId: z.number().nullish(),
});
export type LiveSession = z.infer<typeof LiveSessionSchema>;

const LiveSessionsSchema = z.object({ items: z.array(LiveSessionSchema) });

export async function fetchCohortSessions(cohortId: string): Promise<LiveSession[]> {
  const raw = await bileliApiJson<unknown>(`/cohorts/${cohortId}/live-sessions`);
  return parseApi(LiveSessionsSchema, raw, 'GET /cohorts/{id}/live-sessions').items;
}

export async function createSession(
  cohortId: string,
  body: { title: string; startsAt?: string; kind?: 'TELEGRAM_CALL' | 'EXTERNAL'; joinUrl?: string },
): Promise<LiveSession> {
  const raw = await bileliApiJson<unknown>(`/cohorts/${cohortId}/live-sessions`, {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body),
  });
  return parseApi(LiveSessionSchema, raw, 'POST /cohorts/{id}/live-sessions');
}

export async function goLive(id: string): Promise<LiveSession> {
  const raw = await bileliApiJson<unknown>(`/live-sessions/${id}/go-live`, { method: 'POST' });
  return parseApi(LiveSessionSchema, raw, 'POST /live-sessions/{id}/go-live');
}

export async function endSession(id: string): Promise<LiveSession> {
  const raw = await bileliApiJson<unknown>(`/live-sessions/${id}/end`, { method: 'POST' });
  return parseApi(LiveSessionSchema, raw, 'POST /live-sessions/{id}/end');
}

// Full-set PUT: an ABSENT field clears that column server-side. Explicit `null` is allowed (and meaningful)
// so a caller can preserve one field (send its current value) while clearing the other.
export async function setRecording(
  id: string, body: { recordingUrl?: string | null; notes?: string | null },
): Promise<LiveSession> {
  const raw = await bileliApiJson<unknown>(`/live-sessions/${id}/recording`, {
    method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(body),
  });
  return parseApi(LiveSessionSchema, raw, 'PUT /live-sessions/{id}/recording');
}

/** Hard-delete a session (used as "cancel"). Backend broadcasts a `session` DELETED event. */
export async function deleteSession(id: string): Promise<void> {
  await bileliApiVoid(`/live-sessions/${id}`, { method: 'DELETE' });
}

/** The student's currently-LIVE sessions across active enrollments (course-enriched). */
export async function fetchMyLive(): Promise<LiveSession[]> {
  const raw = await bileliApiJson<unknown>('/me/live');
  return parseApi(LiveSessionsSchema, raw, 'GET /me/live').items;
}
